import {
  Autocomplete,
  Box,
  BoxStatus,
  GraphicFilters,
  Label,
  PieCharts,
  Stack,
  StackRow,
} from "codekit";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";

import {
  useGetDataGraphicAleDocMutation,
  useGetAlertByFilterDocMutation,
  useGetClientByFiltersDocMutation,
} from "../../../../libs/redux/slices/Graficsx/Aledocxx/aledocxxApiSlice";
import { useRedirectGraph } from "../../../../utils/hooks/useRedirectGraph";
import { useFilterGraph } from "../../../../utils/hooks/graphs/useFilterGraph";
import { useDataGraph } from "../../../../utils/hooks/graphs/useDataGraph";

const MODIDXXX = 105;
const PROIDXXX = 101;

const Aledocxx = ({ graphName = "ALERTA VENCIMIENTO DE DOCUMENTOS", graphKeyName = "Aledocxx" }) => {
  const [getDataGraphicAle] = useGetDataGraphicAleDocMutation();
  const [getClientByFilters] = useGetClientByFiltersDocMutation();
  const [getAlertByFilter] = useGetAlertByFilterDocMutation();

  const [resultsClient, setResultsClient] = useState([]);
  const [resultsAlert, setResultsAlert] = useState([]);

  const [dataGraphic, setDataGraphic] = useState([]);
  const [colorsChart, setColorsChart] = useState([]);
  const [total, setTotal] = useState(0);

  const [autoKey, setAutoKey] = useState(0);

  const { handleSetFilter, handleResetPesistFilter } = useFilterGraph();
  const { handleSetFilterJson } = useDataGraph({ MODIDXXX, PROIDXXX });
  const { handleRedirectWithRouter } = useRedirectGraph({ MODIDXXX, PROIDXXX });

  const { control, reset } = useForm({
    mode: "onBlur",
    defaultValues: {
      CLIIDXXX: "",
      ALEIDXXX: "",
    },
  });

  const values = useWatch({ control });

  const searchClient = async (CLIIDXXX, cb) => {
    return await getClientByFilters({ CLIIDXXX })
      .unwrap()
      .then((response) => {
        const list = Array.isArray(response?.data) ? response.data : [];
        setResultsClient(
          list.map((item) => ({
            ...item,
            label: `${item.id ?? ""} - ${item.label ?? ""}`.trim(),
          }))
        );
      })
      .catch((err) => {
        console.log("searchClient catch", err);
        setResultsClient([]);
      })
      .finally(() => cb());
  };

  const searchAlert = async (ALENOMXX, cb) => {
    return await getAlertByFilter({ ALENOMXX })
      .unwrap()
      .then((response) => {
        const list = Array.isArray(response?.data) ? response.data : [];
        setResultsAlert(
          list.map((item) => ({
            ...item,
            label: `${item.id ?? ""} - ${item.label ?? ""}`.trim(),
          }))
        );
      })
      .catch((err) => {
        console.log("searchAlert catch", err);
        setResultsAlert([]);
      })
      .finally(() => cb());
  };

  const normalizeClientId = (raw) => {
    if (!raw) return "";
    return String(raw).includes(" - ") ? String(raw).split(" - ")[0] : String(raw);
  };

  const normalizeAlertId = (raw) => {
    if (!raw) return "";
    const str = String(raw);
    return str.includes(" - ") ? str.split(" - ")[0] : str;
  };

  const colorByLabel = useMemo(
    () => ({
      ROJA: {
        colorCellChart: "error-600",
        bgColorCards: "error-100",
        iconColor: "error-600",
        textColor: "error-600",
      },
      NARANJA: {
        colorCellChart: "warning-600",
        bgColorCards: "warning-400",
        iconColor: "warning-600",
        textColor: "warning-600",
      },
      AMARILLA: {
        colorCellChart: "warning-1000",
        bgColorCards: "warning-100",
        iconColor: "warning-1000",
        textColor: "warning-1000",
      },
      AZUL: {
        colorCellChart: "info-600",
        bgColorCards: "info-100",
        iconColor: "info-600",
        textColor: "info-600",
      },
    }),
    []
  );

  /**
   * Construye filtros para la gráfica (backend)
   */
  const buildFiltersGraphic = useCallback(
    (formValues) => {
      const cli = normalizeClientId(formValues.CLIIDXXX);
      const ale = normalizeAlertId(formValues.ALEIDXXX);

      return {
        ...(cli ? { "CIPO0162.CLIIDXXX": cli } : {}),
        ...(ale ? { "CIPO0162.ALEIDXXX": ale } : {}),
      };
    },
    []
  );

  /**
   * Carga data de la gráfica
   */
  const handleGetData = useCallback(
    async (formValues) => {
      const filters = buildFiltersGraphic(formValues);

      return await getDataGraphicAle({ filters })
        .unwrap()
        .then((response) => {
          const safeData = Array.isArray(response?.data) ? response.data : [];

          const totalData = safeData.reduce(
            (acc, it) => acc + (Number(it.value ?? it.VALUE ?? 0) || 0),
            0
          );

          setDataGraphic(safeData);
          setTotal(totalData);

          setColorsChart(
            safeData.map((item) => {
              const key = String(item.label ?? "").toUpperCase();
              return (
                colorByLabel[key] ?? {
                  colorCellChart: "neutral-500",
                  bgColorCards: "neutral-100",
                  iconColor: "neutral-500",
                  textColor: "neutral-600",
                }
              );
            })
          );
        })
        .catch((err) => {
          console.log("getDataGraphicAle catch", err);
          setDataGraphic([]);
          setColorsChart([]);
          setTotal(0);
        });
    },
    [buildFiltersGraphic, colorByLabel, getDataGraphicAle]
  );

  useEffect(() => {
    handleGetData(values);
  }, [values, handleGetData]);

  const handleClickChart = (context) => {
    const color = String(context?.data?.label ?? "").toUpperCase(); // ROJA|NARANJA|AMARILLA|AZUL
    if (!color) return;

    const cli = normalizeClientId(values?.CLIIDXXX);
    const ale = normalizeAlertId(values?.ALEIDXXX);

    const trackingFilters = buildTrackingFilters({ color, cli, ale });
    persistRedirectFilters(trackingFilters);

    handleRedirectWithRouter();
  };


  const handleClearFilters = () => {
    reset({ CLIIDXXX: "", ALEIDXXX: "" });
    setResultsClient([]);
    setResultsAlert([]);
    setAutoKey((k) => k + 1);
  };

  const handleVewMore = () => {
    const cli = normalizeClientId(values?.CLIIDXXX);
    const ale = normalizeAlertId(values?.ALEIDXXX);

    handleResetPesistFilter();

    let filters = {};
    if (cli) filters = handleSetFilter("filterClient", cli);
    if (ale) filters = handleSetFilter("filterAlert", ale);

    handleSetFilterJson(filters);
    handleRedirectWithRouter();
  };

  const REDIRECT_FILTERS_KEY = "redirect_filters_Myalertx";

  const buildTrackingFilters = ({ color, cli, ale }) => {
    const filters = [
      { "CIPO0162.REGESTXX": { IN: ["ACTIVO"] } },
      ...(color ? [{ "CIPO0162.ALECOLOR": { IN: [color] } }] : []),
      ...(cli ? [{ "CIPO0162.CLIIDXXX": { IN: [cli] } }] : []),
      ...(ale ? [{ "CIPO0162.ALEIDXXX": { IN: [ale] } }] : []),
    ];
    return filters;
  };

  const persistRedirectFilters = (filtersArray) => {
    localStorage.setItem(REDIRECT_FILTERS_KEY, JSON.stringify(filtersArray));
  };


  return (
    <Box spacing={4}>
      <GraphicFilters
        containerName={graphName}
        graphicKeyName={graphKeyName}
        clearFilters={handleClearFilters}
        onClickVerMas={handleVewMore}
      >
        <Box>
          <Box mb={8} borderBottom={1} spacing={2}>
            <Stack gap={3} columnsVariant="form-3">
              <StackRow>
                <Label>Descripción de alerta</Label>
                <Controller
                  control={control}
                  name="ALEIDXXX"
                  render={({ field: { onChange, value } }) => (
                    <Autocomplete
                      key={`alert-${autoKey}`}
                      placeholder="Buscar alerta..."
                      textLoading="Buscando..."
                      textNoResults="No se encontraron registros"
                      minLengthToSearch={2}
                      selected={value}
                      searchResults={resultsAlert}
                      onChangeSearch={(val, cb) => searchAlert(val, cb)}
                      onItemClick={(context) => onChange(`${context.item.label}`)}
                      onResetSelected={() => onChange("")}
                    />
                  )}
                />
              </StackRow>

              <StackRow>
                <Label>Cliente y/o Aliado</Label>
                <Controller
                  control={control}
                  name="CLIIDXXX"
                  render={({ field: { onChange, value } }) => (
                    <Autocomplete
                    key={`client-${autoKey}`}
                      placeholder="Buscar..."
                      textLoading="Buscando..."
                      textNoResults="No se encontraron registros"
                      minLengthToSearch={2}
                      selected={value}
                      searchResults={resultsClient}
                      onChangeSearch={(val, cb) => searchClient(val, cb)}
                      onItemClick={(context) => onChange(`${context.item.label}`)}
                      onResetSelected={() => onChange("")}
                    />
                  )}
                />
              </StackRow>
            </Stack>
          </Box>

          <Box>
            {dataGraphic.length > 0 ? (
              <Stack gap={8}>
                <StackRow>
                  <PieCharts
                    onClickCellChart={handleClickChart}
                    onClickIconCard={handleClickChart}
                    onClickCard={handleClickChart}
                    data={dataGraphic}
                    chartColors={colorsChart}
                    labelOne={String(total)}
                    labelTwo="Alertas"
                    isCurrency={false}
                    titleIcon=""
                  />
                </StackRow>
              </Stack>
            ) : (
              <BoxStatus variant="empty" text="Esta sección está vacía" />
            )}
          </Box>
        </Box>
      </GraphicFilters>
    </Box>
  );
};

export default Aledocxx;
